// src/components/JobsSection.js
import React, { useEffect, useState } from "react";
import JobCard from "./cart/JobCard";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import LoadingAnimation from "./LoadingAnimation";

const JobsSection = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "jobPosts"));
        const jobsData = querySnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((job) => !job.isHidden);
        setJobs(jobsData);
      } catch (error) {
        console.error("❌ Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Determine category from job title
  const getCategoryFromTitle = (title) => {
    const t = title.toLowerCase();

    // Motion Graphics — check BEFORE Graphic Design to avoid overlap
    if (
      t.includes("motion graphic") ||
      t.includes("motion designer") ||
      t.includes("motion design") ||
      t.includes("after effects") ||
      t.includes("aftereffects") ||
      t.includes("2d animation") ||
      t.includes("video editor") ||
      t.includes("video editing") ||
      t.includes("animator")
    ) {
      return "Motion Graphics";
    }

    // Graphic Design
    if (
      t.includes("graphic design") ||
      t.includes("graphics design") ||
      t.includes("graphic designer") ||
      t.includes("graphics designer") ||
      t.includes("photoshop") ||
      t.includes("illustrator") ||
      t.includes("figma") ||
      t.includes("ui/ux") ||
      t.includes("ui designer") ||
      t.includes("ux designer") ||
      t.includes("ui ux") ||
      t.includes("visual designer") ||
      t.includes("brand designer") ||
      t.includes("branding")
    ) {
      return "Graphic Design";
    }

    // Website Development
    if (
      t.includes("front-end") ||
      t.includes("front end") ||
      t.includes("frontend") ||
      t.includes("back-end") ||
      t.includes("back end") ||
      t.includes("backend") ||
      t.includes("full stack") ||
      t.includes("fullstack") ||
      t.includes("full-stack") ||
      t.includes("web developer") ||
      t.includes("web development") ||
      t.includes("website developer") ||
      t.includes("website development") ||
      t.includes("react developer") ||
      t.includes("react.js") ||
      t.includes("reactjs") ||
      t.includes("next.js") ||
      t.includes("nextjs") ||
      t.includes("node.js") ||
      t.includes("nodejs") ||
      t.includes("javascript developer") ||
      t.includes("php developer") ||
      t.includes("laravel") ||
      t.includes("wordpress") ||
      t.includes("mern") ||
      t.includes("mean stack") ||
      t.includes("software developer") ||
      t.includes("software engineer")
    ) {
      return "Website Development";
    }

    return "Other";
  };

  // Filter jobs based on selected category
  const filteredJobs =
    selectedCategory === "All"
      ? jobs
      : jobs.filter((job) => getCategoryFromTitle(job.title) === selectedCategory);

  // All icons are now plain strings (img src compatible)
  const getIconForCategory = (category) => {
    switch (category) {
      case "Website Development":
        return "/assets/Images/desktop_duotone_fill (1).png";
      case "Graphic Design":
        return "/assets/Images/Edit_duotone@2x.png";
      case "Motion Graphics":
        return "/assets/Images/Edit_duotone@2x.png";
      default:
        return "/assets/Images/desktop_duotone_fill (1).png";
    }
  };

  const categories = [
    "All",
    "Website Development",
    "Graphic Design",
    "Motion Graphics"
  ];

  return (
    <div className="job-section padding-container">
      {/* Force all job cards to same height — only targets this section */}
      <style>{`
        .job .Grid-3-box {
          align-items: stretch;
        }
        .job .Grid-3-box > * {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        
        /* Dropdown styles for mobile */
        .category-dropdown {
          width: 100%;
          padding: 12px 16px;
          font-size: 16px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          background-color: white;
          color: #333;
          cursor: pointer;
          outline: none;
          transition: all 0.3s ease;
          font-weight: 500;
        }
        
        .category-dropdown:focus {
          border-color: #FF8000;
          box-shadow: 0 0 0 3px rgba(255, 128, 0, 0.1);
        }
        
        .category-dropdown:hover {
          border-color: #FF8000;
        }
        
        /* Responsive styles */
        @media (max-width: 768px) {
          .top-option {
            display: none !important;
          }
          .mobile-dropdown-container {
            padding : 0px 50px;
            display: block !important;
            margin-bottom: 30px;
          }
        }
        
        @media (min-width: 769px) {
          .mobile-dropdown-container {
            display: none !important;
          }
        }
      `}</style>

      <div className="page-wrapper">
        <div className="job">
          <h1 className="job-h1">
            FEATURED <span className="orange-text">JOBS</span>
          </h1>

          {/* Desktop buttons - visible on large screens */}
          <div className="top-option">
            <button
              className={`rounded-btn ${selectedCategory === "All" ? "active" : ""}`}
              onClick={() => setSelectedCategory("All")}
            >
              All
            </button>

            <button
              className={`rounded-btn ${selectedCategory === "Website Development" ? "active" : ""}`}
              onClick={() => setSelectedCategory("Website Development")}
            >
              Website Development
            </button>

            <button
              className={`rounded-btn ${selectedCategory === "Graphic Design" ? "active" : ""}`}
              onClick={() => setSelectedCategory("Graphic Design")}
            >
              Graphic Design
            </button>

            <button
              className={`rounded-btn ${selectedCategory === "Motion Graphics" ? "active" : ""}`}
              onClick={() => setSelectedCategory("Motion Graphics")}
            >
              Motion Graphics
            </button>
          </div>

          {/* Mobile dropdown - visible on small screens */}
          <div className="mobile-dropdown-container">
            <select
              className="category-dropdown"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category === "All" ? "All Categories" : category}
                </option>
              ))}
            </select>
          </div>

          <div className="Grid-3-box">
            {loading ? (
              <LoadingAnimation text="Loading Jobs..." />
            ) : filteredJobs.length > 0 ? (
              filteredJobs.map((job) => {
                const category = getCategoryFromTitle(job.title);
                return (
                  <JobCard
                    key={job.id}
                    id={job.id}
                    date={job.date}
                    icon={getIconForCategory(category)}
                    category={category}
                    title={job.title}
                    requirements={(job.requirements || []).slice(0, 4)}
                    salary={job.salary || "Based on interview"}
                    gender={job.gender || "Male / Female"}
                  />
                );
              })
            ) : (
              <div style={{
                gridColumn: "1 / -1",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px 20px",
                textAlign: "center",
              }}>
                <img
                  src="/assets/Images/Job hunt-amico.svg"
                  alt="No jobs found"
                  style={{ width: "320px", maxWidth: "100%" }}
                />
                <h3 style={{ fontSize: "20px", fontWeight: "600", color: "#333", marginBottom: "8px" }}>
                  {selectedCategory === "All"
                    ? "No Jobs Available Yet"
                    : `No ${selectedCategory} Jobs Available`}
                </h3>
                <p style={{ fontSize: "15px", color: "#888", maxWidth: "360px" }}>
                  {selectedCategory === "All"
                    ? ""
                    : `We don't have any ${selectedCategory} openings right now. Try another category or check back later.`}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobsSection;